import React from "react";
import {createUseStyles, useTheme} from "react-jss";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";
import {Theme} from "engage-client/theme";
import HostPlayerDisplay from "./HostPlayerDisplay";
import HostViewAll, {ViewAllType} from "./HostViewAll";
import Streak from "./Streak";


const useStyles = createUseStyles<Theme>((theme) => ({
    avgTimeContainer: {
        color: theme.accessoryPanel.dashBoard.sectionTitleColor,
        display: "flex",
        flexDirection: "column",
        background: theme.accessoryPanel.dashBoard.averageTime.player.background,
        padding: 20,
        flex: 2,
        height: "100%",
        minHeight: 260,
    },
    stopwatch: {
        color: theme.accessoryPanel.dashBoard.averageTime.player.timeColor,
        position: "relative",
        width: 150,
        height: 157,
        [theme.breakpoints.down("md")] : {
            width: 134,
            height: 140,
        },
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        marginBottom: 20,
    },
    stopwatchImage: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
    },
    stopwatchTime: {
        display: "flex",
        alignItems: "baseline",
        marginTop: 10,
    },
    hostAvgTimeContainer: {
        color: theme.accessoryPanel.dashBoard.averageTime.host.timeColor,
        display: "flex",
        flexGrow: 1,
        flexDirection: "column",
        background: theme.accessoryPanel.dashBoard.averageTime.host.background,
        alignItems: "center",
        padding: 32,
        [theme.breakpoints.only("xs")] : {
            padding: 20
        },
        height: "100%",
        width: "33.33%"
    },
    title: {
        margin: [18,0,28],
        color: theme.accessoryPanel.dashBoard.sectionTitleColor,
        [theme.breakpoints.only("lg")] : {
            margin: [0,0,12],
        },
        [theme.breakpoints.only("xs")] : {
            margin: [0,0,12],
        },
        fontWeight: theme.accessoryPanel.dashBoard.simpleDashboard ? 700 : 400
    },
    hostTitle: {
        fontWeight: theme.accessoryPanel.dashBoard.simpleDashboard ? 700 : 400,
        color: theme.accessoryPanel.dashBoard.sectionTitleColor,
        minHeight: 36
    },
    centerContent: {
        margin: "auto",
        display: "flex",
        flexDirection: "row",
        [theme.breakpoints.down("lg")] : {
            flexDirection: "column"
        }
    },
    stopwatchContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: 200,
        paddingLeft: 10,
        [theme.breakpoints.down("lg")] : {
            paddingLeft: 0,
            paddingTop: 10
        }
    },
}));

export type AverageTimeProps = {
    averageTimeMS: number
    streak?: number
}

export default function AverageTime({averageTimeMS, streak = 0}:AverageTimeProps) {
    const theme:Theme = useTheme();
    const classes = useStyles();
    return (
        <div className={classes.avgTimeContainer}>
            <DisplayText variant={TextVariant.s18reg} className={classes.title}>STREAK + ANSWER TIME</DisplayText>
            <div className={classes.centerContent}>
                <Streak streak={streak}/>
                <div className={classes.stopwatchContainer}>
                    <div className={classes.stopwatch}>
                        <img className={classes.stopwatchImage} src={theme.accessoryPanel.dashBoard.averageTime.stopWatchSVGPath} alt="Stopwatch"/>
                        <div className={classes.stopwatchTime}>
                            <DisplayText variant={TextVariant.s44reg}>{Math.round(averageTimeMS/100)/10}</DisplayText>
                            <DisplayText variant={TextVariant.s34reg}>s</DisplayText>
                        </div>
                    </div>
                    <DisplayText variant={TextVariant.s18reg}>Average Answer Time</DisplayText>
                </div>
            </div>
        </div>
    );
}

type HostAverageTimeProps = AverageTimeProps & {
    names:string[]
    maxToShow:number
}

export function HostAverageTime({averageTimeMS, names, maxToShow}:HostAverageTimeProps) {
    const theme:Theme = useTheme();
    const classes = useStyles();
    return (
        <div className={classes.hostAvgTimeContainer}>
            <div className={classes.stopwatch}>
                <img className={classes.stopwatchImage} src={theme.accessoryPanel.dashBoard.averageTime.stopWatchSVGPath} alt="Stopwatch"/>
                <div className={classes.stopwatchTime}>
                    <DisplayText variant={TextVariant.s44reg}>{Math.round(averageTimeMS/100)/10}</DisplayText>
                    <DisplayText variant={TextVariant.s34reg}>s</DisplayText>
                </div>
            </div>
            <DisplayText variant={TextVariant.s18reg} className={classes.hostTitle}>FASTEST AVG TIME</DisplayText>
            {names.slice(0,maxToShow).map((name, index) => <HostPlayerDisplay name={name} key={index}/>)}
            <HostViewAll number={averageTimeMS} names={names} maxToShow={maxToShow} type={ViewAllType.Time}/>
        </div>
    );
}